import { CHANGE_SCALE_INDEX, DELETE_COMPONENTS, LOAD_PICASSO_STATE, REQUEST_PAINTING_SCROLL_POSITION, SELECT_ALL_COMPONENTS, SELECT_COMPONENT, SELECT_LINK, SELECT_POINT, UNSELECT_ALL, UNSELECT_COMPONENT, UNSELECT_LINK, UNSELECT_POINT, UPDATE_COMPONENT_VISIBILITY, UPDATE_COMPONENTS, UPDATE_COMPONENTS_ALIGNMENT, UPDATE_COMPONENTS_RESIZE_OPTION, UPDATE_LINKS } from "./constants";
import { AlignType, ResizeOptions } from "./enums";
import { IComponent, ILink } from "./interfaces";
import { ComponentChangeType, IChangeScaleIndexAction, IDeleteComponentsAction, ILoadStateAction, IRequestPaintingScrollPositionAction, ISelectAllComponentsAction, ISelectComponentAction, ISelectLinkAction, ISelectPointAction, IUnselectAllAction, IUnselectComponentAction, IUnselectLinkAction, IUnselectPointAction, IUpdateComponentsAction, IUpdateComponentsAlignmentAction, IUpdateComponentsResizeOptionAction, IUpdateComponentVisibilityAction, IUpdateLinksAction, LinkChangeType, ScrollPosition, SelectablePoint } from "./types";

/**
 * Načte stav Picassa.
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param components Seznam komponent k vykreslení.
 * @param links Seznam spojení k vykreslení.
 * @param selectedComponentIndex Index komponenty, která má být označena.
 * @param selectedLinkIndex Index linku, který má být označen.
 */
export const createLoadPicassoStateAction = (picassoInstanceKey: string, components: readonly IComponent[], links: readonly ILink[], selectedComponentIndex?: number, selectedLinkIndex?: number): ILoadStateAction => ({
    type: LOAD_PICASSO_STATE,
    payload: {
        components,
        links,
        selectedComponentIndex,
        selectedLinkIndex,
    },
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Akce označí danou komponentu.
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param component Komponenta k označení.
 * @param reselect Hodnota určuje, zdali se má označená komponenta přidat do pole již označených komponent (false) či jestli má vytvořit nové pole (true).
 */
export const createSelectComponentAction = (picassoInstanceKey: string, component: IComponent, reselect: boolean): ISelectComponentAction => ({
    type: SELECT_COMPONENT,
    payload: {
        component,
        reselect,
    },
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Akce odoznačí vybranou komponentu
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param componentIndex Index komponenty k odoznačení.
 */
export const createUnselectComponentAction = (picassoInstanceKey: string, componentIndex: number): IUnselectComponentAction => ({
    type: UNSELECT_COMPONENT,
    payload: {
        componentIndex,
    },
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Akce označí vybrané spojení.
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param link Spojení k označení.
 * @param reselect Hodnota určuje, zdali se má označené spojení přidat do pole již označených spojení (false) či jestli má vytvořit nové pole (true).
 */
export const createSelectLinkAction = (picassoInstanceKey: string, link: ILink, reselect: boolean): ISelectLinkAction => ({
    type: SELECT_LINK,
    payload: {
        link,
        reselect,
    },
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Akce odoznačí vybrané spojení.
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param linkIndex Index spojení pro odoznačení.
 */
export const createUnselectLinkAction = (picassoInstanceKey: string, linkIndex: number): IUnselectLinkAction => ({
    type: UNSELECT_LINK,
    payload: {
        linkIndex,
    },
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Upraví pozici/rozměry komponenty.
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param changedComps Seznam komponent k upravení a jejich nové hodnoty.
 */
export const createUpdateComponentsAction = (picassoInstanceKey: string, changedComps: readonly ComponentChangeType[]): IUpdateComponentsAction => ({
    type: UPDATE_COMPONENTS,
    payload: {
        changedComps,
    },
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Odstraní komponenty.
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param componentsToDelete Seznam všech kompoent k odstranění.
 */
export const createDeleteComponentsAction = (picassoInstanceKey: string, componentsToDelete: readonly number[]): IDeleteComponentsAction => ({
    type: DELETE_COMPONENTS,
    payload: {
        componentsToDelete,
    },
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Upraví tvar spojení.
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param changedLinks Seznam spojení k upravení a jejich nové množiny bodů.
 */
export const createUpdateLinksAction = (picassoInstanceKey: string, changedLinks: readonly LinkChangeType[]): IUpdateLinksAction => ({
    type: UPDATE_LINKS,
    payload: {
        changedLinks,
    },
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Označí bod spojení.
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param selectedPoint Bod k uložení, jakožto označený.
 */
export const createSelectPointAction = (picassoInstanceKey: string, selectedPoint: SelectablePoint | null): ISelectPointAction => ({
    type: SELECT_POINT,
    payload: {
        selectedPoint,
    },
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Odoznačí bod spojení.
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 */
export const createUnselectPointAction = (picassoInstanceKey: string): IUnselectPointAction => ({
    type: UNSELECT_POINT,
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Změní hodnotu přiblížení plátna Picassa
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param newScaleIndex Nová hodnota přiblížení.
 */
export const createChangeScaleIndexAction = (picassoInstanceKey: string, newScaleIndex: number): IChangeScaleIndexAction => ({
    type: CHANGE_SCALE_INDEX,
    payload: {
        newScaleIndex,
    },
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Označí všechny přímé potomky zvoleného rodiče.
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param parentIndex Index předka
 */
export const createSelectAllComponentsAction = (picassoInstanceKey: string, parentIndex: number): ISelectAllComponentsAction => ({
    type: SELECT_ALL_COMPONENTS,
    payload: {
        parentIndex,
    },
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Odoznačí všechny entity na plátně.
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 */
export const createUnselectAllAction = (picassoInstanceKey: string): IUnselectAllAction => ({
    type: UNSELECT_ALL,
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Změní pozici scrollbarů plátna Picassa
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param requestedScrollPosition Nová pozice scrollbarů.
 */
export const createRequestPaintingScrollPositionAction = (picassoInstanceKey: string, requestedScrollPosition: ScrollPosition): IRequestPaintingScrollPositionAction => ({
    type: REQUEST_PAINTING_SCROLL_POSITION,
    payload: {
        requestedScrollPosition,
    },
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Změní viditelnost picasso komponenty.
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param componentIndex Index komponenty ke změně viditelnosti.
 * @param isVisible Nová hodnota viditelnosti.
 */
export const createUpdateComponentVisibilityAction = (picassoInstanceKey: string, componentIndex: number, isVisible: boolean): IUpdateComponentVisibilityAction => ({
    type: UPDATE_COMPONENT_VISIBILITY,
    payload: {
        componentIndex,
        isVisible,
    },
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Vyvolá akci pro změnu zakotvení picasso komponenty.
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param componentIndex Index komponenty ke změně zakotvení.
 * @param align Nová hodnota zakotvení.
 */
export const createUpdateComponentsAlignmentAction = (picassoInstanceKey: string, componentIndex: number, align: AlignType): IUpdateComponentsAlignmentAction => ({
    type: UPDATE_COMPONENTS_ALIGNMENT,
    payload: {
        componentIndex,
        align,
    },
    meta: {
        picassoInstanceKey,
    },
});

/**
 * Vyvolá akci pro změnu možnosti resizování picasso komponenty.
 * @param picassoInstanceKey Klíč instance picassa pro přístup ke storu.
 * @param componentIndex Index komponenty ke změně možnosti resizu.
 * @param resizeOption Nová hodnota resizu.
 * @param noCssHeight Určuje, jestli se má nastavovat CSS výška picasso komponenty.
 * @param noCssWidth Určuje, jestli se má nastavovat CSS šířka picasso komponenty.
 */
export const createUpdateComponentsResizeOptionAction = (picassoInstanceKey: string, componentIndex: number, resizeOption: ResizeOptions, noCssHeight: boolean, noCssWidth: boolean): IUpdateComponentsResizeOptionAction => ({
    type: UPDATE_COMPONENTS_RESIZE_OPTION,
    payload: {
        componentIndex,
        resizeOption,
        noCssHeight,
        noCssWidth,
    },
    meta: {
        picassoInstanceKey,
    },
});
